import React, { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { setMeta, injectJsonLd } from '../utils/seo';

const Home = lazy(()=>import('../pages/Home'));
const About = lazy(()=>import('../pages/About'));
const Services = lazy(()=>import('../pages/Services'));
const ServiceDetail = lazy(()=>import('../pages/ServiceDetail'));
const Book = lazy(()=>import('../pages/Book'));
const Contact = lazy(()=>import('../pages/Contact'));
const FAQ = lazy(()=>import('../pages/FAQ'));
const Blog = lazy(()=>import('../pages/Blog'));
const BlogDetail = lazy(()=>import('../pages/BlogDetail'));
const CaseStudies = lazy(()=>import('../pages/CaseStudies'));
const Privacy = lazy(()=>import('../pages/Privacy'));
const Terms = lazy(()=>import('../pages/Terms'));
const Admin = lazy(()=>import('../pages/AdminDashboard'));
const NotFound = lazy(()=>import('../pages/NotFound'));

export const routes = [
  { path: '/', element: <Home/>, title: 'Bloomington Water Damage Restoration | Bloomington Restoration Services', description: 'Emergency water damage restoration, flood cleanup, mold remediation in Bloomington, IN. 24/7 response. Call +1 (812) 555-0123.', canonical: 'https://www.bloomrestoration.com/' },
  { path: '/about', element: <About/>, title: 'About | Bloomington Restoration Services', description: 'Learn about our team, mission, and restoration process in Bloomington, Indiana.', canonical: 'https://www.bloomrestoration.com/about' },
  { path: '/services', element: <Services/>, title: 'Services | Water Damage Restoration Bloomington IN', description: 'Full-service water damage, flood cleanup, mold remediation, sewage cleanup, and repairs in Bloomington, Ellettsville, Bedford, Martinsville, Spencer.', canonical: 'https://www.bloomrestoration.com/services' },
  { path: '/services/:slug', element: <ServiceDetail/>, title: 'Service | Bloomington Restoration Services', description: 'Detailed scopes, timelines, and FAQs for our restoration services in Bloomington, IN.', canonical: 'https://www.bloomrestoration.com/services' },
  { path: '/book', element: <Book/>, title: 'Book Emergency Service | Bloomington Restoration Services', description: 'Request emergency water damage service in Bloomington, IN. Fast dispatch 24/7.', canonical: 'https://www.bloomrestoration.com/book' },
  { path: '/contact', element: <Contact/>, title: 'Contact | Bloomington Restoration Services', description: 'Call, email, or visit our Bloomington office. Serving Monroe County & nearby cities.', canonical: 'https://www.bloomrestoration.com/contact' },
  { path: '/faq', element: <FAQ/>, title: 'FAQ | Bloomington Restoration Services', description: 'Answers about insurance, safety, pricing, and restoration steps for Bloomington homeowners.', canonical: 'https://www.bloomrestoration.com/faq' },
  { path: '/blog', element: <Blog/>, title: 'Blog | Water Damage Tips for Bloomington Indiana', description: 'Guides on flood cleanup, drying basements, mold prevention, and safety in Bloomington, IN.', canonical: 'https://www.bloomrestoration.com/blog' },
  { path: '/blog/:slug', element: <BlogDetail/>, title: 'Article | Bloomington Restoration Services', description: 'In-depth restoration advice for Bloomington & Monroe County homeowners.', canonical: 'https://www.bloomrestoration.com/blog' },
  { path: '/case-studies', element: <CaseStudies/>, title: 'Case Studies | Bloomington Restoration Services', description: 'Before/after project photos from Bloomington, Ellettsville, Bedford, and Martinsville.', canonical: 'https://www.bloomrestoration.com/case-studies' },
  { path: '/privacy', element: <Privacy/>, title: 'Privacy Policy | Bloomington Restoration Services', description: 'Privacy practices for our Bloomington restoration website and services.', canonical: 'https://www.bloomrestoration.com/privacy' },
  { path: '/terms', element: <Terms/>, title: 'Terms of Service | Bloomington Restoration Services', description: 'Website terms and service conditions.', canonical: 'https://www.bloomrestoration.com/terms' },
  { path: '/admin', element: <Admin/>, title: 'Admin (Placeholder) | Bloomington Restoration Services', description: 'Internal dashboard placeholder — add your routes and auth later.', canonical: 'https://www.bloomrestoration.com/admin' },
  { path: '*', element: <NotFound/>, title: '404 Not Found | Bloomington Restoration Services', description: 'Page not found.' }
];

function MetaUpdater() {
  const location = useLocation();
  useEffect(() => {
    const r = routes.find(rt => rt.path === location.pathname) || {};
    setMeta({
      title: r.title || 'Bloomington Restoration Services',
      description: r.description || 'Restoration in Bloomington, IN',
      canonical: r.canonical || `https://www.bloomrestoration.com${location.pathname}`,
      image: '/assets/images/og-default.svg'
    });
    if (location.pathname === '/') {
      injectJsonLd({
        "@context":"https://schema.org",
        "@type":"LocalBusiness",
        "name":"Bloomington Restoration Services",
        "telephone":"+1-812-555-0123",
        "address":{
          "@type":"PostalAddress",
          "streetAddress":"121 W Kirkwood Ave",
          "addressLocality":"Bloomington",
          "addressRegion":"IN","postalCode":"47404","addressCountry":"US"
        }
      });
    }
  }, [location]);
  return null;
}

export default function AppRoutes() {
  return (
    <Suspense fallback={<div>Loading…</div>}>
      <MetaUpdater />
      <Routes>
        {routes.map(r => <Route key={r.path} path={r.path} element={r.element} />)}
      </Routes>
    </Suspense>
  );
}
